import { z } from 'zod';
import { EventStatus, ClientRole } from '../types/index.js';
export const createEventSchema = z
    .object({
    name: z.string().min(1).max(255),
    eventTypeId: z.string().uuid(),
    startAt: z.coerce.date(),
    endAt: z.coerce.date().nullable().optional(),
    venueId: z.string().uuid().optional(),
    guestCount: z.number().int().positive().optional(),
    budget: z.number().positive().optional(),
    status: z.nativeEnum(EventStatus).optional(),
    parentEventId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
})
    .refine((d) => !d.endAt || d.endAt >= d.startAt, {
    message: 'endAt must be on or after startAt',
    path: ['endAt'],
});
const BUDGET_VISIBILITY_OPTIONS = ['full', 'summary', 'hidden'];
export const updateEventSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    eventTypeId: z.string().uuid().optional(),
    startAt: z.coerce.date().optional(),
    endAt: z.coerce.date().nullable().optional(),
    venueId: z.string().uuid().optional(),
    guestCount: z.number().int().positive().optional(),
    budget: z.number().positive().optional(),
    status: z.nativeEnum(EventStatus).optional(),
    notes: z.string().max(2000).optional(),
    budgetVisibility: z.enum(BUDGET_VISIBILITY_OPTIONS).optional(),
})
    .refine((d) => !d.startAt || !d.endAt || d.endAt >= d.startAt, {
    message: 'endAt must be on or after startAt',
    path: ['endAt'],
});
export const ALLOWED_LOGO_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/svg+xml',
];
/** Asking for somewhere to upload a record's logo (an event's, a vendor's). */
export const logoUploadUrlSchema = z.object({
    fileName: z.string().min(1).max(500),
    mimeType: z.enum(ALLOWED_LOGO_MIME_TYPES),
});
/** Attaching the uploaded logo to its record, or clearing it (null). */
export const setLogoSchema = z.object({
    s3Key: z.string().min(1).max(1024).nullable(),
});
export const eventLogoUploadUrlSchema = logoUploadUrlSchema;
export const setEventLogoSchema = setLogoSchema;
export const createEventUserSchema = z.object({
    userId: z.string().uuid(),
    clientRole: z.nativeEnum(ClientRole),
    permissions: z
        .object({
        canApprovePlan: z.boolean().optional(),
        canApproveInvoice: z.boolean().optional(),
        canMakePayments: z.boolean().optional(),
        canMakeChanges: z.boolean().optional(),
        canPrint: z.boolean().optional(),
        canManageInvites: z.boolean().optional(),
        canManageGuests: z.boolean().optional(),
        canManageTasks: z.boolean().optional(),
        canManageSeating: z.boolean().optional(),
        canMessage: z.boolean().optional(),
        canMessageVendors: z.boolean().optional(),
    })
        .optional(),
});
export const updateEventUserSchema = z.object({
    clientRole: z.nativeEnum(ClientRole).optional(),
    permissions: z
        .object({
        canApprovePlan: z.boolean().optional(),
        canApproveInvoice: z.boolean().optional(),
        canMakePayments: z.boolean().optional(),
        canMakeChanges: z.boolean().optional(),
        canPrint: z.boolean().optional(),
        canManageInvites: z.boolean().optional(),
        canManageGuests: z.boolean().optional(),
        canManageTasks: z.boolean().optional(),
        canManageSeating: z.boolean().optional(),
        canMessage: z.boolean().optional(),
        canMessageVendors: z.boolean().optional(),
    })
        .optional(),
});
// ─── Event Contacts ─────────────────────────────────────────────────────────
export const addEventContactSchema = z.object({
    userId: z.string().uuid(),
    title: z.string().min(1).max(100),
    isPrimary: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const updateEventContactSchema = z.object({
    title: z.string().min(1).max(100).optional(),
    isPrimary: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
// ─── Event Tags ─────────────────────────────────────────────────────────────
export const tagCodeSchema = z
    .string()
    .min(1)
    .max(50)
    .regex(/^[A-Z0-9_]+$/, 'Code must be uppercase letters, numbers, and underscores only');
export const createEventTagSchema = z.object({
    code: tagCodeSchema,
    description: z.string().min(1).max(255),
    type: z.enum(['TEXT', 'DATE', 'NUMBER']),
    value: z.string().max(1000).optional(),
});
export const updateEventTagSchema = z.object({
    value: z.string().max(1000).nullable().optional(),
    code: tagCodeSchema.optional(),
    description: z.string().min(1).max(255).optional(),
    type: z.enum(['TEXT', 'DATE', 'NUMBER']).optional(),
});
export const createEventTypeDefaultTagSchema = z.object({
    code: tagCodeSchema,
    description: z.string().min(1).max(255),
    type: z.enum(['TEXT', 'DATE', 'NUMBER']),
    sortOrder: z.number().int().optional(),
});
export const updateEventTypeDefaultTagSchema = z.object({
    code: tagCodeSchema.optional(),
    description: z.string().min(1).max(255).optional(),
    type: z.enum(['TEXT', 'DATE', 'NUMBER']).optional(),
    sortOrder: z.number().int().optional(),
});
//# sourceMappingURL=event.js.map